import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  useInvoicesStats,
  useInvoicesPage,
  INVOICES_PAGE_SIZE,
} from "@/hooks/useInvoices";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Plus, FileText, Clock, CheckCircle, AlertCircle, FileEdit, Lock, Download, ChevronLeft, ChevronRight, Mail, X, Loader2 } from "lucide-react";
// jsPDF lazy-loaded inside handleDownloadPdf to keep ~400KB out of initial bundle
import { toast } from "sonner";
import { useTrialLimit } from "@/hooks/useTrialLimit";
import { useUserTier } from "@/hooks/useUserTier";
import AppHeader from "@/components/AppHeader";
import AppFooter from "@/components/AppFooter";
import DeletionBanner from "@/components/DeletionBanner";
import { useEffectiveBetaMode } from "@/hooks/useEffectiveBetaMode";
import AccountantSendDialog from "@/components/AccountantSendDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import RetryingInvoicesSection from "@/components/RetryingInvoicesSection";

// Page size centralized in useInvoices.ts so AppHeader prefetch + Dashboard
// reads stay in lockstep. Re-export under the local name to avoid touching
// every reference in this file.
const PAGE_SIZE = INVOICES_PAGE_SIZE;

const statusConfig: Record<string, { label: string; icon: any; variant: "default" | "secondary" | "destructive" | "outline" | "success" | "warning" | "info" }> = {
  draft: { label: "Ciornă", icon: FileEdit, variant: "secondary" },
  pending: { label: "În așteptare", icon: Clock, variant: "warning" },
  validated: { label: "Validată", icon: CheckCircle, variant: "success" },
  error: { label: "Eroare", icon: AlertCircle, variant: "destructive" },
  // Matches InvoiceDetail badge — Loader2 spins via the className below.
  retrying: { label: "Reîncearcă", icon: Loader2, variant: "info" },
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { effectiveBetaMode: BETA_MODE } = useEffectiveBetaMode();
  const { hasReachedLimit, remaining, limit, isUnlimited } = useTrialLimit();
  const { tierName, hasTier, isLoading: tierLoading } = useUserTier();
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [acctDialogOpen, setAcctDialogOpen] = useState(false);

  const toggleSelect = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // Reset to page 0 when filter changes
  useEffect(() => { setPage(0); }, [statusFilter]);

  const handleDownloadPdf = useCallback(async (e: React.MouseEvent, inv: any) => {
    e.stopPropagation();
    try {
      const { data: items, error } = await supabase
        .from("invoice_items")
        .select("*")
        .eq("invoice_id", inv.id)
        .order("sort_order");
      if (error) throw error;
      const { generateInvoicePdf } = await import("@/lib/generateInvoicePdf");
      const doc = generateInvoicePdf({
        invoice_number: inv.invoice_number,
        issue_date: inv.issue_date,
        due_date: inv.due_date,
        seller_name: inv.seller_name,
        seller_cui: inv.seller_cui,
        seller_address: inv.seller_address,
        seller_city: inv.seller_city,
        seller_county: inv.seller_county,
        buyer_name: inv.buyer_name,
        buyer_cui: inv.buyer_cui,
        buyer_address: inv.buyer_address,
        buyer_city: inv.buyer_city,
        buyer_county: inv.buyer_county,
        subtotal: Number(inv.subtotal),
        vat_total: Number(inv.vat_total),
        total: Number(inv.total),
        items: (items || []).map((i) => ({
          item_name: i.item_name,
          quantity: Number(i.quantity),
          unit_price: Number(i.unit_price),
          vat_rate: Number(i.vat_rate),
          unit_code: i.unit_code || "C62",
        })),
      });
      doc.save(`factura-${inv.invoice_number || inv.id.slice(0, 8)}.pdf`);
      toast.success("PDF descărcat!");
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Eroare la generarea PDF-ului");
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
    if (!loading && user && !tierLoading && !hasTier) navigate("/select-plan");
  }, [user, loading, navigate, tierLoading, hasTier]);

  // Stats + paginated list now flow through shared hooks (see useInvoices.ts).
  // Same query keys as before, so AppHeader's hover-prefetch remains a hit.
  const { data: allInvoices } = useInvoicesStats(user?.id);
  const { data: paginatedResult, isLoading } = useInvoicesPage(
    user?.id,
    (statusFilter as "draft" | "pending" | "validated" | "error" | null) || null,
    page,
  );

  const invoices = paginatedResult?.data ?? [];
  const totalCount = paginatedResult?.count ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Stats from all invoices
  const totalInvoices = allInvoices?.length ?? 0;
  const draftCount = allInvoices?.filter(i => i.status === "draft").length ?? 0;
  const pendingCount = allInvoices?.filter(i => i.status === "pending").length ?? 0;
  const validatedCount = allInvoices?.filter(i => i.status === "validated").length ?? 0;
  const errorCount = allInvoices?.filter(i => i.status === "error").length ?? 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <DeletionBanner />

      <main className="container py-6 max-w-2xl">
          <RetryingInvoicesSection userId={user?.id} />

          {/* Downgrade info banner */}
          {!isUnlimited && limit !== null && totalInvoices > limit && !hasReachedLimit && (
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-5 mb-6 space-y-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-primary shrink-0" />
                <span className="font-heading font-semibold text-sm">Ai mai multe facturi decât permite planul tău actual</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Planul <span className="font-semibold text-foreground">{tierName}</span> include {limit} facturi/lună.
                Toate facturile tale existente rămân accesibile — le poți <span className="font-semibold text-foreground">vizualiza</span>, <span className="font-semibold text-foreground">edita</span>, <span className="font-semibold text-foreground">descărca</span> ca PDF/XML și <span className="font-semibold text-foreground">trimite la ANAF</span> oricând.
                Crearea de facturi noi este limitată la {limit}/lună.
              </p>
              <Button size="sm" variant="outline" className="text-xs" onClick={() => navigate("/select-plan")}>
                Vezi Planurile
              </Button>
            </div>
          )}

          {/* Trial limit banner */}
          {hasReachedLimit ? (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5 mb-8 text-center space-y-3">
              <div className="flex items-center justify-center gap-2">
                <Lock className="h-5 w-5 text-destructive" />
                <span className="font-heading font-bold">Limită Atinsă</span>
              </div>
               <p className="text-sm text-muted-foreground">
                 Ai folosit toate cele <span className="font-semibold text-foreground">{limit}</span> facturi din planul tău luna aceasta.
                 {BETA_MODE
                   ? " În perioada beta, planurile plătite nu sunt disponibile. Limita se resetează luna viitoare."
                   : " Fă upgrade pentru a continua."}
              </p>
              {!BETA_MODE && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button className="font-semibold" onClick={() => navigate("/select-plan")}>
                      Fă Upgrade
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Alege un plan cu mai multe facturi</TooltipContent>
                </Tooltip>
              )}
            </div>
          ) : (
            <>
              {!isUnlimited && remaining <= 3 && remaining > 0 && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-center mb-4">
                  Mai ai <span className="font-bold text-primary">{remaining}</span> {remaining === 1 ? "factură" : "facturi"} rămase luna aceasta din planul {tierName || "tău"}.
                </div>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="lg"
                    className="w-full h-14 text-base font-semibold mb-8"
                    onClick={() => navigate("/invoice/new")}
                  >
                    <Plus className="mr-2 h-5 w-5" />
                    Creează Factură Nouă
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Deschide formularul pentru o factură nouă</TooltipContent>
              </Tooltip>
            </>
          )}

          {/* Stats / Filters */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
            {[
              { label: "Total", value: totalInvoices, filter: null },
              { label: "Ciorne", value: draftCount, filter: "draft" },
              { label: "În așteptare", value: pendingCount, filter: "pending" },
              { label: "Validate", value: validatedCount, filter: "validated" },
              { label: "Erori", value: errorCount, filter: "error" },
            ].map((s, i) => (
              <Tooltip key={i}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setStatusFilter(statusFilter === s.filter ? null : s.filter)}
                    className={`rounded-xl border p-4 text-center transition-colors cursor-pointer ${
                      statusFilter === s.filter
                        ? "border-primary bg-primary/10 ring-1 ring-primary/30"
                        : "border-border bg-card hover:border-primary/30"
                    }`}
                  >
                    <div className="text-2xl font-bold font-heading">{s.value}</div>
                    <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {statusFilter === s.filter ? "Arată toate facturile" : `Filtrează doar ${s.label.toLowerCase()}`}
                </TooltipContent>
              </Tooltip>
            ))}
          </div>

          {/* Invoice List */}
          <h2 className="font-heading text-lg font-semibold mb-4">
            {statusFilter === "draft" ? "Ciorne" : statusFilter === "pending" ? "Facturi În Așteptare" : statusFilter === "validated" ? "Facturi Validate" : statusFilter === "error" ? "Facturi cu Erori" : "Facturi Recente"}
            {totalCount > 0 && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({totalCount})
              </span>
            )}
          </h2>
          {isLoading ? (
            <div className="space-y-3" aria-busy="true" aria-label="Se încarcă facturile">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between rounded-xl border border-border bg-card p-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Skeleton className="h-4 w-4 rounded" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-2/3" />
                      <Skeleton className="h-3 w-1/3" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-6 w-24 rounded-full" />
                    <Skeleton className="h-7 w-7 rounded-lg" />
                  </div>
                </div>
              ))}
            </div>
          ) : !invoices.length && page === 0 ? (
            statusFilter ? (
              <div className="text-center py-16 border border-dashed border-border rounded-xl">
                <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground mb-4">
                  Nici o factură în această categorie.
                </p>
                <Button variant="outline" onClick={() => setStatusFilter(null)}>
                  Arată toate
                </Button>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-primary/30 bg-gradient-to-br from-primary/5 via-card to-card p-8 sm:p-12 text-center">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 ring-1 ring-primary/20">
                  <FileText className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-2">Bun venit pe EZ Factura! 👋</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6 leading-relaxed">
                  Nu ai încă facturi. Creează prima ta factură în mai puțin de 30 de secunde — completezi datele clientului, adaugi produsele și o trimiți direct la ANAF.
                </p>

                <div className="grid sm:grid-cols-3 gap-3 max-w-lg mx-auto mb-6 text-left">
                  <div className="rounded-lg bg-card border border-border p-3">
                    <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary text-xs font-bold mb-2">1</div>
                    <p className="text-xs text-muted-foreground leading-snug">Completezi datele clientului (sau cauți după CUI)</p>
                  </div>
                  <div className="rounded-lg bg-card border border-border p-3">
                    <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary text-xs font-bold mb-2">2</div>
                    <p className="text-xs text-muted-foreground leading-snug">Adaugi produsele/serviciile și prețurile</p>
                  </div>
                  <div className="rounded-lg bg-card border border-border p-3">
                    <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary text-xs font-bold mb-2">3</div>
                    <p className="text-xs text-muted-foreground leading-snug">Trimiți direct la ANAF cu un singur click</p>
                  </div>
                </div>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="lg" className="font-semibold" onClick={() => navigate("/invoice/new")}>
                      <Plus className="mr-2 h-5 w-5" />
                      Creează Prima Factură
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Începe prin a crea prima ta factură</TooltipContent>
                </Tooltip>

                <p className="text-xs text-muted-foreground mt-4">
                  💡 Tip: completează datele firmei tale în{" "}
                  <button onClick={() => navigate("/settings")} className="text-primary hover:underline font-medium">
                    Setări
                  </button>{" "}
                  o singură dată — apoi se completează automat pe orice factură.
                </p>
              </div>
            )
          ) : (
            <>
              <div className="space-y-3">
                {invoices.map((inv) => {
                  const sc = statusConfig[inv.status];
                  const Icon = sc.icon;
                  const isSelected = selectedIds.has(inv.id);
                  return (
                    <Tooltip key={inv.id}>
                      <TooltipTrigger asChild>
                        <div
                          className={`flex items-center justify-between rounded-xl border p-4 hover:border-primary/30 transition-colors cursor-pointer ${
                            isSelected ? "border-primary bg-primary/5" : "border-border bg-card"
                          }`}
                          onClick={() => navigate(`/invoice/${inv.id}`)}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleSelect(inv.id)}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`Selectează factura ${inv.invoice_number || ""}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">
                                {inv.buyer_name || "Factură nouă"}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {inv.invoice_number || "Fără număr"} · {new Date(inv.created_at).toLocaleDateString("ro-RO")}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <span className="font-heading font-semibold text-sm">
                              {Number(inv.total).toLocaleString("ro-RO", { style: "currency", currency: "RON" })}
                            </span>
                            <Badge variant={sc.variant} className="gap-1 text-xs">
                              <Icon className={`h-3 w-3 ${inv.status === "retrying" ? "animate-spin" : ""}`} />
                              {sc.label}
                            </Badge>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={(e) => handleDownloadPdf(e, inv)}
                                  className="inline-flex items-center justify-center h-11 w-11 sm:h-9 sm:w-9 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                                  aria-label="Descarcă factura ca PDF"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>Descarcă PDF</TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>Click pentru a vedea detaliile facturii</TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                    className="gap-1"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Anterior
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Pagina <span className="font-semibold text-foreground">{page + 1}</span> din <span className="font-semibold text-foreground">{totalPages}</span>
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage(p => p + 1)}
                    className="gap-1"
                  >
                    Următor
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </>
          )}
      </main>

      {/* Floating bulk-action bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 rounded-2xl border border-primary/30 bg-card shadow-lg px-4 py-3 flex items-center gap-3">
          <span className="text-sm font-medium">
            {selectedIds.size} {selectedIds.size === 1 ? "factură selectată" : "facturi selectate"}
          </span>
          <Button size="sm" onClick={() => setAcctDialogOpen(true)}>
            <Mail className="h-4 w-4 mr-1.5" /> Trimite la contabil
          </Button>
          <button
            onClick={clearSelection}
            className="inline-flex items-center justify-center h-11 w-11 sm:h-9 sm:w-9 rounded-lg hover:bg-muted text-muted-foreground"
            aria-label="Anulează selecția"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <AccountantSendDialog
        open={acctDialogOpen}
        onOpenChange={setAcctDialogOpen}
        invoiceIds={Array.from(selectedIds)}
        contextLabel={`${selectedIds.size} ${selectedIds.size === 1 ? "factură selectată" : "facturi selectate"}`}
        onSent={clearSelection}
      />

      <AppFooter />
    </div>
  );
};

export default Dashboard;
